import { useState } from "react";

export type TabKey = "agent" | "characters" | "settings" | "scenes" | "edit";

export interface TabDef {
  key: TabKey;
  label: string;
  description: string;
  icon: React.ReactNode;
}

export const TABS: TabDef[] = [
  {
    key: "agent",
    label: "Agent",
    description: "Pitch a video; the agent fills outline, cast, scenes, and edit.",
    icon: <AgentIcon />,
  },
  {
    key: "characters",
    label: "Characters",
    description: "Named reference cast members with consistent visual identity.",
    icon: <CharacterIcon />,
  },
  {
    key: "settings",
    label: "Settings",
    description: "Named places that anchor scenes visually.",
    icon: <PlaceIcon />,
  },
  {
    key: "scenes",
    label: "Scenes",
    description: "Storyboards that pull in characters and places.",
    icon: <SceneIcon />,
  },
  {
    key: "edit",
    label: "Edit",
    description: "Arrange generated clips into a sequence.",
    icon: <EditIcon />,
  },
];

interface SidebarProps {
  mode: "gallery" | "workspace";
  projectName: string | null;
  active: TabKey;
  onChange: (key: TabKey) => void;
  onGallery: () => void;
  counts: Record<TabKey, number>;
}

export function Sidebar({
  mode,
  projectName,
  active,
  onChange,
  onGallery,
  counts,
}: SidebarProps) {
  const [hover, setHover] = useState<TabKey | null>(null);

  return (
    <aside className="sidebar">
      <a className="sidebar-brand" href="/" aria-label="VideoCataz">
        <img
          className="sidebar-brand-logo"
          src="/videocataz_logo.png"
          alt="VideoCataz"
        />
      </a>

      <button
        className={`tab-btn ${mode === "gallery" ? "active" : ""}`}
        onClick={onGallery}
        data-testid="project-gallery"
        style={{ marginBottom: 4 }}
      >
        <span className="icon"><GalleryIcon /></span>
        <span style={{ flex: 1 }}>Project Gallery</span>
      </button>

      <div className="section-title">Workspace</div>
      {projectName && mode === "workspace" && (
        <div className="tiny" style={{ padding: "0 12px 6px", color: "#fff", fontWeight: 700 }}>
          {projectName}
        </div>
      )}
      {TABS.map((tab) => {
        const isActive = mode === "workspace" && tab.key === active;
        const isHover = tab.key === hover;
        return (
          <button
            key={tab.key}
            className={`tab-btn ${isActive ? "active" : ""}`}
            onClick={() => onChange(tab.key)}
            onMouseEnter={() => setHover(tab.key)}
            onMouseLeave={() => setHover(null)}
            data-testid={`tab-${tab.key}`}
            disabled={mode !== "workspace"}
            style={mode !== "workspace" ? { opacity: 0.45 } : undefined}
          >
            <span className="icon">{tab.icon}</span>
            <span style={{ flex: 1 }}>{tab.label}</span>
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 700 }}>
              {mode === "workspace" ? counts[tab.key] : "—"}
            </span>
          </button>
        );
      })}

      <div className="sidebar-footer">
        {hover && mode === "workspace" && (
          <div className="tiny muted" style={{ padding: "0 12px 8px" }}>
            {TABS.find((t) => t.key === hover)?.description}
          </div>
        )}
        <div className="sidebar-commit" title={__BUILD_COMMIT_AT__}>
          {formatCommitAt(__BUILD_COMMIT_AT__)}
        </div>
      </div>
    </aside>
  );
}

function formatCommitAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function GalleryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function AgentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
    </svg>
  );
}

function CharacterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
    </svg>
  );
}

function PlaceIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-7 8-13a8 8 0 0 0-16 0c0 6 8 13 8 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function SceneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M3 9h18" />
      <path d="M9 9v9" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16M4 12h16M4 18h10" />
      <path d="M18 14l2 2-4 4h-2v-2z" />
    </svg>
  );
}
